import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, auth, type CapabilitiesResponse, type Company, type Me } from './api';

interface SessionValue {
  ready: boolean;
  me: Me | null;
  company: Company | null;
  caps: CapabilitiesResponse | null;
  loadingCaps: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  switchCompany: (id: string) => void;
  refresh: () => Promise<void>;
}

const Ctx = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [me, setMe] = useState<Me | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(auth.companyId);
  const [caps, setCaps] = useState<CapabilitiesResponse | null>(null);
  const [loadingCaps, setLoadingCaps] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const capsSeq = useRef(0);

  const company = useMemo(
    () => me?.companies.find((c) => c.id === companyId) ?? me?.companies[0] ?? null,
    [me, companyId],
  );

  const loadMe = useCallback(async () => {
    const data = await api.get<Me>('/me');
    setMe(data);
    // Si no hay empresa elegida, se toma la primera.
    const initial = auth.companyId && data.companies.some((c) => c.id === auth.companyId)
      ? auth.companyId
      : data.companies[0]?.id ?? null;
    auth.setCompany(initial);
    setCompanyId(initial);
  }, []);

  const loadCaps = useCallback(async (id: string | null) => {
    if (!id) {
      setCaps(null);
      return;
    }
    const seq = ++capsSeq.current;
    setLoadingCaps(true);
    try {
      auth.setCompany(id);
      const data = await api.get<CapabilitiesResponse>('/capabilities');
      if (seq === capsSeq.current) setCaps(data);
    } finally {
      if (seq === capsSeq.current) setLoadingCaps(false);
    }
  }, []);

  // Arranque: si hay token, se recupera la sesión.
  useEffect(() => {
    (async () => {
      if (!auth.token) {
        setReady(true);
        return;
      }
      try {
        await loadMe();
      } catch {
        auth.setToken(null);
      } finally {
        setReady(true);
      }
    })();
  }, [loadMe]);

  // Cuando cambia la empresa activa, se recarga su relación de capacidades.
  useEffect(() => {
    if (company) void loadCaps(company.id);
  }, [company, loadCaps]);

  const login = useCallback(
    async (email: string, password: string) => {
      setError(null);
      try {
        const { token } = await api.post<{ token: string }>('/auth/login', { email, password });
        auth.setToken(token);
        await loadMe();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo iniciar sesión.');
        throw e;
      }
    },
    [loadMe],
  );

  const logout = useCallback(() => {
    auth.setToken(null);
    auth.setCompany(null);
    setMe(null);
    setCaps(null);
    setCompanyId(null);
  }, []);

  const switchCompany = useCallback((id: string) => {
    auth.setCompany(id);
    setCompanyId(id);
  }, []);

  const refresh = useCallback(async () => {
    await loadMe();
    if (company) await loadCaps(company.id);
  }, [loadMe, loadCaps, company]);

  const value: SessionValue = {
    ready,
    me,
    company,
    caps,
    loadingCaps,
    error,
    login,
    logout,
    switchCompany,
    refresh,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession fuera de SessionProvider');
  return v;
}
